import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

import checkout from './modules/checkout.js'
import profile from './modules/profile.js'

/**
 * Reads a JSON response without ever throwing.
 *
 * `fetch(...).catch(console.error)` yields `undefined`, and a route can answer
 * 200 with an empty body (that was the /api/userStats bug) — `res.json()`
 * throws on both, and an error payload on a 401/500 is not a value worth
 * committing either. Every one of those cases is "we have no data", which is
 * what null says, so a single bad response degrades one field instead of
 * rejecting out through fetchAllUserInfo() and aborting its callers.
 */
async function readJson(res) {
  if (!res || !res.ok) return null
  return res.json().catch(() => null)
}

export default new Vuex.Store({
  state: {
    nav: {
      open: false
    },
    purchase: {
      modalOpen: false,
      // Which bundle type the modal opened for ('credits' | 'gold') — the
      // actual bundles for sale live in the checkout module, fetched from
      // Stripe (GET /api/checkout/prices), not hardcoded here.
      modalItems: 'credits',
    },
    user: {
      token: null,
      id: null,
      email: null,
      avatar: null,
      username: null,
    },
    dbInfo: {
      credits: null,
      gold: null,
    },
    guilds: {
      list: [],
      lastUpdated: null,
    },
    stats: {
      mostPlayed: [],
      totalWins: 0,
      totalGames: 0,
      winrate: 0,
    }
  },
  getters: {
    getToken: state => {
      let token = state.user.token
      if(!token) {
      // Fallback only, not reactive: document.cookie isn't a Vuex-tracked
      // dependency, so this branch won't trigger getter re-evaluation on its own.
      let row = document.cookie.split('; ').find(row => row.startsWith('token'))
        if(row) {
          token = row.split('=')[1]
        }
      }
      return token
    },
    getUser: state => state.user,

    /**
     * Who is signed in, resolvable *before* fetchUserInfo has run.
     *
     * Same shape of fallback as getToken above, and for the same reason: a
     * router guard runs before any component has dispatched anything, so the
     * store is still empty on a cold load. The id is already on disk —
     * fetchUserInfo reads this exact key — so waiting for an action to copy
     * it into state would be waiting for nothing.
     *
     * That is what lets `/profile/me` resolve to a real id during the first
     * navigation rather than after the page has painted.
     *
     * Not reactive through the localStorage branch (same caveat as getToken):
     * once fetchUserInfo commits, `state.user.id` takes over and this getter
     * re-evaluates normally.
     *
     * A cached entry can outlive its Discord token, so this answers *which*
     * user, never *whether* one is signed in — `getToken` decides that, and
     * callers must ask it first. Read alone this getter reports a lapsed
     * session as a live one; that is how `/profile/me` used to show a
     * signed-out visitor their own profile with no way to tell it from being
     * signed in. See createProfileGuard().
     */
    getUserId: state => {
      if (state.user.id) return state.user.id
      try {
        const cached = JSON.parse(localStorage.getItem('user'))
        return (cached && cached.id) || null
      } catch (err) {
        // A corrupt entry is a logged-out user, not a crash.
        return null
      }
    },
    getdbInfo: state => state.dbInfo,
    getGuilds: state => state.guilds,
    getStats: state => state.stats,
  },
  mutations: {
    setToken(state, token) {
      state.user.token = token
    },
    setUser(state, user) {
      // Without an id there is no user to set, and the default-avatar maths
      // below would throw on `BigInt(undefined)` — which used to take out the
      // whole mounted hook of whatever page committed it, leaving a blank
      // screen rather than a logged-out one.
      if (!user || !user.id) {
        state.user.id = null
        state.user.avatar = null
        state.user.username = null
        return
      }

      state.user.id = user.id
      // Discord users with no custom avatar have `avatar: null` — fall back to their
      // default avatar (indexed off the user ID under Discord's post-2023 numbering).
      state.user.avatar = user.avatar
        ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=64`
        : `https://cdn.discordapp.com/embed/avatars/${Number((BigInt(user.id) >> 22n) % 6n)}.png`
      state.user.username = user.global_name || user.username
    },
    setDBInfo(state, dbInfo) {
      state.dbInfo = { ...dbInfo }
      // Add helper fields
      state.dbInfo.credits = dbInfo.balance || 0
      state.dbInfo.gold = dbInfo.goldBalance || 0
    },
    /**
     * Applies a theme locally, ahead of the server agreeing.
     *
     * The whole profile reads its theme from `dbInfo.theme`, so picking one in
     * the customizer used to do nothing visible until the write round-tripped
     * and dbInfo was refetched — half a second of a page that hadn't reacted
     * to the thing you just clicked. The picker sets this immediately and puts
     * it back if the save is refused.
     *
     * Replaces the object rather than assigning the key, so the change is
     * reactive even on a dbInfo that arrived without a `theme` at all.
     */
    setAppliedTheme(state, themeId) {
      state.dbInfo = { ...state.dbInfo, theme: themeId }
    },
    setStats(state, stats) {
      state.stats = { ...stats }
    },
    togglePurchaseModal(state) {
      state.purchase.modalOpen = !state.purchase.modalOpen
    },
    setPurchaseModalOpen(state, open) {
      state.purchase.modalOpen = open
    },
    setModalItems(state, modalItems) {
      state.purchase.modalItems = modalItems
    },
    setGuilds(state, guilds) {
      state.guilds.lastUpdated = Date.now()
      state.guilds.list = guilds
    }
  },
  actions: {
    openPurchaseModal({ commit }, { type = 'credits' } = {}) {
      commit('setModalItems', type)
      commit('setPurchaseModalOpen', true)
    },
    login(context, token) {
      context.commit('setToken', token)
    },
    logout(context) {
      document.cookie = 'token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      context.commit('setToken', null)
      window.location.reload()
    },
    async fetchUserInfo(context, force) {
      let userData;
      if(!force) {
        // Retrieve user data from local storage
        userData = JSON.parse(localStorage.getItem('user'))
        if(userData && userData.date < Date.now() - 1000 * 60 * 60 * 24) {
          // If user data is more than than 24 hours old, fetch new data
          userData = undefined
        }
      }

      // If we don't have user data, or we're forcing a refresh, fetch it from the API
      if(this.getters.getToken && !userData) {
          let res = await fetch('https://discord.com/api/users/@me', {
              headers: {
                  authorization: `Bearer ${this.getters.getToken}`
              }
          }).catch(console.error)

          // A rejected token still returns a JSON *body* — `{"message":"401:
          // Unauthorized","code":0}` — so without an ok check that error
          // payload got cached as the user and then blew up in setUser(). An
          // expired Discord token is the ordinary case here (they lapse on
          // their own), not an edge case, so treat it as logged out: drop the
          // stale cache so the next load doesn't replay it.
          if(!res || !res.ok) {
            localStorage.removeItem('user')
            context.commit('setUser', null)
            return
          }

          userData = await res.json()
          userData.date = Date.now()

          // Save the user data to localStorage
          localStorage.setItem('user', JSON.stringify(userData))
      }
      // Update user
      context.commit('setUser', userData)
    },
    async fetchDBInfo(context) {
      // `.id`, not just `getUser` — that getter returns the user *object*,
      // which is always truthy even when logged out, so this used to fire a
      // request for `userID=null`.
      if(this.getters.getToken && this.getters.getUser.id) {
        let res = await fetch('/api/userInfo?userID=' + this.getters.getUser.id, {
          method: 'GET',
          headers: {
              authorization: `Bearer ${this.getters.getToken}`
          }
        }).catch(console.error)

        let response = await readJson(res)
        // Update user
        if(response) context.commit('setDBInfo', response)
      }
    },
    async fetchStats(context) {
      if(this.getters.getToken && this.getters.getUser.id) {
        let res = await fetch('/api/userStats?userID=' + this.getters.getUser.id, {
          method: 'GET',
          headers: {
              authorization: `Bearer ${this.getters.getToken}`
          }
        }).catch(console.error)

        let response = await readJson(res)
        // Update user
        if(response) context.commit('setStats', response)
      }
    },
    /**
     * Sequential because each step needs the one before it (dbInfo and stats
     * are keyed off the id fetchUserInfo resolves), but each is independently
     * caught: this must never reject.
     *
     * Callers await it *before* loading anything of their own — App.vue's
     * initialize() fetches premium status after it, Profile.vue's mounted()
     * fetches the reward track after it — so a rejection here doesn't fail
     * one field, it silently skips everything downstream. That's exactly what
     * happened when /api/userStats answered with an empty body: no crown
     * anywhere, and a reward track stuck in its "not loaded yet" state with
     * no Claim buttons on it.
     */
    async fetchAllUserInfo({ dispatch }) {
      await dispatch('fetchUserInfo', false).catch(console.error)
      await dispatch('fetchDBInfo').catch(console.error)
      await dispatch('fetchStats').catch(console.error)
    },
  },
  modules: {
    checkout,
    profile
  }
})
